import { useMutation } from "@tanstack/react-query";
import { Radio } from "lucide-react";
import React from "react";
import { useEffect, useState } from "react";
import { api, jsonBody } from "../lib/api";
import type {
  ConnectivityType,
  LiveSessionRecord,
  LiveSessionStatus,
  StudyDetail,
} from "../lib/types";
import { Button, Card, Field } from "./ui";

type LiveSessionForm = {
  status: LiveSessionStatus;
  startedAt: string;
  endedAt: string;
  technicalUserEmail: string;
  doctorUserEmail: string;
  connectivityType: ConnectivityType;
  meetingReference: string;
  incidentDescription: string;
  result: string;
  recordingUrl: string;
  recordingAuthorized: boolean;
};

function toLocalDateTime(value: string | null) {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function newSessionForm(study: StudyDetail): LiveSessionForm {
  return {
    status: "PLANIFICADA",
    startedAt: "",
    endedAt: "",
    technicalUserEmail: study.technicianEmail || "",
    doctorUserEmail: study.doctorEmail,
    connectivityType: study.connectivityType,
    meetingReference: "",
    incidentDescription: "",
    result: "",
    recordingUrl: "",
    recordingAuthorized: false,
  };
}

function sessionForm(session: LiveSessionRecord): LiveSessionForm {
  return {
    status: session.status,
    startedAt: toLocalDateTime(session.startedAt),
    endedAt: toLocalDateTime(session.endedAt),
    technicalUserEmail: session.technicalUserEmail,
    doctorUserEmail: session.doctorUserEmail,
    connectivityType: session.connectivityType,
    meetingReference: session.meetingReference || "",
    incidentDescription: session.incidentDescription || "",
    result: session.result || "",
    recordingUrl: session.recordingUrl || "",
    recordingAuthorized: session.recordingAuthorized,
  };
}

export function LiveSessionPanel({
  study,
  onMessage,
  onRefresh,
}: {
  study: StudyDetail;
  onMessage: (message: string) => void;
  onRefresh: () => Promise<unknown>;
}) {
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [form, setForm] = useState<LiveSessionForm>(() =>
    newSessionForm(study),
  );
  useEffect(() => {
    if (!sessionId) setForm(newSessionForm(study));
  }, [
    study.id,
    study.technicianEmail,
    study.doctorEmail,
    study.connectivityType,
    sessionId,
  ]);
  const cancelEditing = () => {
    setSessionId(null);
    setForm(newSessionForm(study));
  };
  const selectSession = (session: LiveSessionRecord) => {
    setSessionId(session.id);
    setForm(sessionForm(session));
  };
  const mutation = useMutation({
    mutationFn: () =>
      api(
        `/api/studies/${study.id}/live-sessions${sessionId ? `/${sessionId}` : ""}`,
        {
          method: sessionId ? "PUT" : "POST",
          ...jsonBody({
            ...form,
            startedAt: form.startedAt ? new Date(form.startedAt) : null,
            endedAt: form.endedAt ? new Date(form.endedAt) : null,
            meetingReference: form.meetingReference || null,
            incidentDescription: form.incidentDescription || null,
            result: form.result || null,
            recordingUrl: form.recordingUrl || null,
          }),
        },
      ),
    onSuccess: async () => {
      onMessage(
        sessionId
          ? "Sesi\u00f3n en vivo actualizada."
          : "Sesi\u00f3n en vivo creada.",
      );
      await onRefresh();
      cancelEditing();
    },
    onError: (error) =>
      onMessage(
        error instanceof Error
          ? error.message
          : "No fue posible guardar la sesi\u00f3n.",
      ),
  });
  return (
    <div className="two-column">
      <Card
        title={
          sessionId ? `Editar sesi\u00f3n #${sessionId}` : "Nueva sesi\u00f3n"
        }
      >
        <div className="form-grid two">
          <Field label="Estado">
            <select
              aria-label="Estado"
              value={form.status}
              onChange={(event) =>
                setForm({
                  ...form,
                  status: event.target.value as LiveSessionStatus,
                })
              }
            >
              {[
                "PLANIFICADA",
                "INICIADA",
                "FINALIZADA",
                "CANCELADA",
                "FALLIDA",
              ].map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </Field>
          <Field label="Conectividad">
            <select
              aria-label="Conectividad"
              value={form.connectivityType}
              onChange={(event) =>
                setForm({
                  ...form,
                  connectivityType: event.target.value as ConnectivityType,
                })
              }
            >
              {[
                "WIFI_CLINICA",
                "ETHERNET",
                "HOTSPOT_PERSONAL",
                "SIM_CORPORATIVA",
                "MIFI",
                "SIN_CONECTIVIDAD",
                "OTRO",
              ].map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </Field>
          <Field label={"T\u00e9cnica"} required>
            <input
              aria-label={"T\u00e9cnica"}
              type="email"
              required
              value={form.technicalUserEmail}
              onChange={(event) =>
                setForm({ ...form, technicalUserEmail: event.target.value })
              }
            />
          </Field>
          <Field label={"M\u00e9dico"} required>
            <input
              aria-label={"M\u00e9dico"}
              type="email"
              required
              value={form.doctorUserEmail}
              onChange={(event) =>
                setForm({ ...form, doctorUserEmail: event.target.value })
              }
            />
          </Field>
          <Field label="Inicio">
            <input
              aria-label="Inicio"
              type="datetime-local"
              value={form.startedAt}
              onChange={(event) =>
                setForm({ ...form, startedAt: event.target.value })
              }
            />
          </Field>
          <Field label="Fin">
            <input
              aria-label="Fin"
              type="datetime-local"
              value={form.endedAt}
              onChange={(event) =>
                setForm({ ...form, endedAt: event.target.value })
              }
            />
          </Field>
          <Field label={"Referencia de reuni\u00f3n"}>
            <input
              aria-label={"Referencia de reuni\u00f3n"}
              value={form.meetingReference}
              onChange={(event) =>
                setForm({ ...form, meetingReference: event.target.value })
              }
            />
          </Field>
          <Field label="Incidente">
            <textarea
              aria-label="Incidente"
              rows={3}
              value={form.incidentDescription}
              onChange={(event) =>
                setForm({ ...form, incidentDescription: event.target.value })
              }
            />
          </Field>
          <Field label="Resultado">
            <textarea
              aria-label="Resultado"
              rows={3}
              value={form.result}
              onChange={(event) =>
                setForm({ ...form, result: event.target.value })
              }
            />
          </Field>
        </div>
        <div className="checkbox-grid">
          <label>
            <input
              type="checkbox"
              checked={form.recordingAuthorized}
              onChange={(event) =>
                setForm({ ...form, recordingAuthorized: event.target.checked })
              }
            />{" "}
            {"Grabaci\u00f3n autorizada"}
          </label>
        </div>
        <div className="button-row">
          <Button busy={mutation.isPending} onClick={() => mutation.mutate()}>
            <Radio size={16} />{" "}
            {sessionId ? "Actualizar sesi\u00f3n" : "Crear sesi\u00f3n"}
          </Button>
          {sessionId && (
            <Button
              variant="secondary"
              disabled={mutation.isPending}
              onClick={cancelEditing}
            >
              {"Cancelar edici\u00f3n"}
            </Button>
          )}
        </div>
      </Card>
      <Card title="Sesiones registradas">
        {study.liveSessions.length ? (
          <div className="compact-list">
            {study.liveSessions.map((session) => (
              <div className="compact-row vertical" key={session.id}>
                <div>
                  <strong>
                    {session.status} &middot; {session.connectivityType}
                  </strong>
                  <small>
                    {session.technicalUserEmail} &rarr;{" "}
                    {session.doctorUserEmail}
                  </small>
                  <small>
                    {session.startedAt
                      ? new Date(session.startedAt).toLocaleString("es-CO")
                      : "Sin inicio"}{" "}
                    &middot;{" "}
                    {session.result ||
                      session.incidentDescription ||
                      "Sin resultado"}
                  </small>
                </div>
                <Button
                  variant={session.id === sessionId ? "primary" : "secondary"}
                  onClick={() => selectSession(session)}
                >
                  {session.id === sessionId ? "Editando" : "Editar"}
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted">No hay sesiones en vivo registradas.</p>
        )}
      </Card>
    </div>
  );
}
